import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  agendarTurma,
  criarAvaliacao,
  criarTurma,
  definirGrade,
  definirTurmaAtual,
  excluirTurma,
  lerFiltros,
  lerGradeDaTurma,
  lerRegistroDasTurmas,
  lerTurmasDoAdmin,
  lerVersoesDaGrade,
  type DisciplinaDisponivel,
  type DisciplinaNaGrade,
  type FiltrosDoAdminPos,
  type GradeDaTurma,
  type MudancaDaTurma,
  type RespostaDaExclusao,
  type RespostaDaGrade,
  type RespostaDoInicio,
  type TurmaDoAdmin,
  type VersaoDaGrade,
} from "../lib/adminPos";
import Carregando from "./Carregando";
import AdminPosQuestoes from "./AdminPosQuestoes";
import AdminPosCronograma from "./AdminPosCronograma";

// Admin › Faculdade › Turmas — duas das três telas que a secretaria pediu, e
// Victor mandou fazer em 14/09/2026: **definir a turma atual** e **editar a
// grade**.
//
// ---------------------------------------------------------------------------
// Por que as DUAS numa aba só
// ---------------------------------------------------------------------------
// A grade é da TURMA e a turma atual é do CURSO, mas as duas partem do mesmo
// lugar: a lista de turmas daquele curso. Em abas separadas, o operador
// escolheria a turma duas vezes — e a pergunta "qual é a atual?" é justamente o
// contexto de quem vai mexer na grade de uma delas.
//
// ---------------------------------------------------------------------------
// A lista de turmas NÃO aparece antes do curso
// ---------------------------------------------------------------------------
// Regra de Victor, em 14/09: *"jamais mostrar a lista de turmas sem antes
// selecionar o curso"*. E ela tem número: as 59 turmas usam só **20 números**,
// com "Turma 1" existindo em **9 cursos**. Sem o curso, a lista oferece nove
// opções com o mesmo nome — o operador não tem como escolher, e o que ele
// escolher vai parecer certo.
//
// ---------------------------------------------------------------------------
// ZERO regra aqui
// ---------------------------------------------------------------------------
// Quem resolve a precedência da turma atual (horário marcado que já começou →
// escolha da equipe → maior número COM grade → maior número), quem valida que a
// disciplina é do curso e quem conta os alunos alcançados por uma remoção é o
// BANCO (000224, 000316). A tela mostra e pergunta.
//
// ---------------------------------------------------------------------------
// A turma vira na HORA MARCADA (000316, G6 do plano de Produtos)
// ---------------------------------------------------------------------------
// Cada turma pode ter o instante a partir do qual recebe as matrículas novas, e
// o encerramento da turma atual é esse mesmo instante — a tela o mostra uma vez,
// no topo do curso, e não o guarda em lugar nenhum. A hora é SEMPRE a de
// Brasília, e quem a converte é o banco: o campo manda "2026-10-01T00:00" como
// foi digitado, e as frases voltam prontas. Nenhum `new Date` aqui.
//
// ---------------------------------------------------------------------------
// Reordenar é com SETAS, não arrastando
// ---------------------------------------------------------------------------
// Arrastar exige mouse e mira; as setas funcionam no teclado, no celular e para
// quem usa leitor de tela — e a grade tem até 38 disciplinas (medido), onde
// arrastar da posição 35 para a 2 é pior que clicar. A ordem que vale é a
// POSIÇÃO na lista enviada, e ela é atributo do conjunto: por isso a tela manda
// a lista completa, nunca "mova esta".

/**
 * O aviso do banco sem o prefixo `aviso:` — e com a primeira letra MAIÚSCULA.
 * O banco o escreve em minúscula, como continuação do prefixo; na tela ele vem
 * depois de outra frase ("Turma 2 excluída. …"), e a foto de 25/09 mostrou
 * "…continuam no curso. a turma 2 era a atual."
 */
function fraseDoAviso(aviso: string | null | undefined): string {
  const t = (aviso ?? "").replace(/^aviso:\s*/, "");
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
}

export default function AdminPosTurmas() {
  const [filtros, setFiltros] = useState<FiltrosDoAdminPos | null>(null);
  // O curso pode vir na QUERY (`?curso=`): é o link "preencher o cronograma" da
  // página do produto (000309). Vir do link é uma escolha explícita — a tela
  // continua não escolhendo curso por conta própria.
  const [params] = useSearchParams();
  const [curso, setCurso] = useState<string>(() => params.get("curso") ?? "");
  const [turmas, setTurmas] = useState<TurmaDoAdmin[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [recado, setRecado] = useState<{ tom: "ok" | "alerta"; texto: string } | null>(null);
  const [editando, setEditando] = useState<string | null>(null);
  // O CRONOGRAMA (000272), ao lado da grade. Um de cada vez: os dois abertos
  // mostrariam a mesma turma em duas telas, e o cronograma LISTA a grade que a
  // outra está editando — o operador leria estado velho sem saber.
  const [cronogramaDe, setCronogramaDe] = useState<string | null>(null);
  // O HORÁRIO DE INÍCIO (000316), terceiro editor da linha. Também um de cada
  // vez: o operador que abre o horário de uma turma está pensando na virada, não
  // na grade dela.
  const [inicioDe, setInicioDe] = useState<string | null>(null);
  const [criandoTurma, setCriandoTurma] = useState(false);
  // A turma que o operador pediu para excluir, com o ENSAIO que o banco
  // devolveu. Enquanto isto não for nulo, o popup está aberto.
  const [aExcluir, setAExcluir] = useState<{ turma: TurmaDoAdmin; ensaio: RespostaDaExclusao } | null>(
    null,
  );
  const [excluindo, setExcluindo] = useState(false);
  // A recusa da EXCLUSÃO fica no popup, e não no recado da tela: a recusa por
  // falta de motivo precisa aparecer ao lado do campo que falta (000299).
  const [recusaDaExclusao, setRecusaDaExclusao] = useState<string | null>(null);
  // O REGISTRO das turmas do curso (000299): quem criou, quem excluiu e por
  // quê. Relido a cada recarga da lista, que é o que todo ato desta tela faz.
  const [registro, setRegistro] = useState<{ total: number; mudancas: MudancaDaTurma[] } | null>(null);

  useEffect(() => {
    void lerFiltros().then((f) => {
      setFiltros(f);
      // NÃO escolhe um curso por conta própria: a tela entra pedindo a escolha,
      // e a lista de turmas só existe depois dela.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recarregar = useCallback(async () => {
    // Sem curso não há o que carregar — e `carregando` tem de voltar a falso,
    // senão a tela fica girando para sempre no estado de entrada, que é
    // justamente o estado que toda visita começa.
    if (!curso) {
      setTurmas(null);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    const t = await lerTurmasDoAdmin(curso);
    setTurmas(t);
    setErro(t === null);
    setCarregando(false);
    // O registro é peça SECUNDÁRIA: se ele falhar, a lista de turmas continua —
    // o erro de uma leitura não troca a tela inteira.
    const r = await lerRegistroDasTurmas(curso);
    setRegistro(r.ok ? { total: r.total ?? 0, mudancas: r.mudancas ?? [] } : null);
  }, [curso]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  async function tornarAtual(turma: TurmaDoAdmin, desfazer: boolean) {
    setRecado(null);
    const r = await definirTurmaAtual(curso, desfazer ? null : turma.turma_id);
    if (!r.ok) {
      setRecado({ tom: "alerta", texto: r.detalhe ?? "Não deu para mudar a turma atual." });
      return;
    }
    // O `aviso:` é o terceiro estado do princípio 2 — aplicado COM divergência
    // conhecida. Ele vem quando a turma escolhida não tem grade, e some da tela
    // no caso comum.
    setRecado(
      r.aviso
        ? { tom: "alerta", texto: fraseDoAviso(r.aviso) }
        : {
            tom: "ok",
            texto: desfazer
              ? "Voltou a derivar: o curso usa a turma mais recente que tem grade."
              : `A turma ${r.numero} passou a ser a atual. As matrículas novas caem nela.`,
          },
    );
    await recarregar();
  }

  // A resposta do banco vira o recado da tela INTEIRA, e não fica no editor: o
  // que o operador precisa ler é para onde vão as matrículas agora — e o editor
  // fecha, porque o ato acabou.
  async function concluirInicio(r: RespostaDoInicio) {
    setInicioDe(null);
    setRecado({
      tom: r.aviso ? "alerta" : "ok",
      texto: [r.detalhe, fraseDoAviso(r.aviso)].filter(Boolean).join(" "),
    });
    await recarregar();
  }

  // O número NÃO é escolhido pela tela: o banco usa max + 1, e a distinção tem
  // caso real (pos-rotinas-simples tem 1, 2, 3 e 7 — count + 1 daria 5). Deixar
  // a tela propor um número seria criar a segunda definição de "a próxima".
  async function novaTurma() {
    setCriandoTurma(true);
    setRecado(null);
    const r = await criarTurma(curso);
    setCriandoTurma(false);
    if (!r.ok) {
      setRecado({ tom: "alerta", texto: r.detalhe ?? "Não deu para criar a turma." });
      return;
    }
    setRecado({
      tom: "alerta",
      texto:
        `Turma ${r.numero} criada. ` +
        fraseDoAviso(r.aviso),
    });
    await recarregar();
    // Abre a grade da turma nova: ela nasce vazia, e o passo seguinte é sempre
    // pôr disciplina nela.
    if (r.turma_id) setEditando(r.turma_id);
  }

  // O primeiro clique NÃO apaga: ele pede o ENSAIO ao banco, e é a resposta
  // dele que enche o popup. Montar o texto na tela seria escrever uma segunda
  // vez o que a função já sabe — quantas disciplinas saem da grade, se ela é a
  // turma atual —, e as duas versões divergiriam.
  async function pedirExclusao(turma: TurmaDoAdmin) {
    setRecado(null);
    const ensaio = await excluirTurma(turma.turma_id, false);
    if (ensaio.motivo === "turma_com_matricula") {
      // Recusa do banco, com o número de alunos. Ela não vira popup: não há o
      // que confirmar, e o operador precisa é da frase.
      setRecado({ tom: "alerta", texto: ensaio.detalhe ?? "Esta turma tem matrículas." });
      return;
    }
    if (ensaio.motivo !== "confirme") {
      setRecado({ tom: "alerta", texto: ensaio.detalhe ?? "Não deu para excluir a turma." });
      return;
    }
    setRecusaDaExclusao(null);
    setAExcluir({ turma, ensaio });
  }

  async function confirmarExclusao(motivo: string) {
    if (!aExcluir) return;
    setExcluindo(true);
    setRecusaDaExclusao(null);
    const r = await excluirTurma(aExcluir.turma.turma_id, true, motivo);
    setExcluindo(false);
    if (!r.ok) {
      // O popup FICA aberto na recusa: fechar jogaria fora o motivo que a
      // pessoa escreveu e a frase do banco que diz o que falta.
      setRecusaDaExclusao(r.detalhe ?? "Não deu para excluir a turma.");
      return;
    }
    setAExcluir(null);
    setRecado({
      tom: "alerta",
      texto:
        `Turma ${r.numero} excluída. ` +
        `As ${r.disciplinas_desvinculadas ?? 0} avaliação(ões) continuam no curso. ` +
        fraseDoAviso(r.aviso),
    });
    setEditando(null);
    await recarregar();
  }

  if (erro) {
    return (
      <div className="rounded-cf-lg border border-erro-borda bg-erro-fundo p-4 text-sm text-erro-tinta">
        Não conseguimos carregar as turmas. Tente recarregar a página.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header>
        <h2 className="font-display text-lg font-bold text-titulo">Turmas e grade</h2>
        <p className="mt-1 text-sm text-cinza">
          Qual turma recebe as matrículas novas, e quais disciplinas cada turma cursa.
        </p>
      </header>

      <div className="surface flex flex-wrap items-end gap-4 rounded-cf-lg p-4">
        <div className="min-w-60 flex-1">
        <label className="block text-xs font-bold tracking-widest text-cinza uppercase" htmlFor="curso-das-turmas">
          Curso
        </label>
        <select
          id="curso-das-turmas"
          value={curso}
          onChange={(e) => {
            setCurso(e.target.value);
            // Trocar de curso ZERA a lista, e o loader volta: a lista do curso
            // anterior não pode ficar na tela enquanto a do novo carrega.
            setTurmas(null);
            setEditando(null);
            // O cronograma fecha junto com a grade ao trocar de curso. Sem isto
            // ele ficava aberto mostrando a turma do curso ANTERIOR — e a prova
            // de tela pegou: o painel continuava desenhado depois de a lista
            // abaixo já ser de outro curso.
            setCronogramaDe(null);
            setInicioDe(null);
            setRecado(null);
          }}
          className="mt-1.5 h-11 w-full max-w-lg rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
        >
          {/* Sem opção "todos": a lista de turmas não existe antes do curso. */}
          <option value="">Escolha o curso…</option>
          {(filtros?.cursos ?? []).map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.nome ?? c.slug}
            </option>
          ))}
        </select>
        </div>
        {/* Criar turma só faz sentido com o curso escolhido — e o botão diz isso
            em vez de sumir: botão que aparece e some conforme o estado obriga o
            operador a descobrir sozinho quando ele existe. */}
        <button
          type="button"
          onClick={novaTurma}
          data-criar-turma
          disabled={!curso || criandoTurma}
          className={curso && !criandoTurma ? "pill-cta min-h-11 px-5" : "pill-desligada min-h-11 px-5"}
        >
          {criandoTurma ? "Criando…" : "Criar turma"}
        </button>
      </div>

      {recado && (
        <p
          role="status"
          className={`rounded-cf-lg p-3 text-sm ${
            recado.tom === "ok"
              ? "bg-aviso-fundo text-aviso-tinta"
              : "bg-alerta-fundo text-alerta-tinta"
          }`}
        >
          {recado.texto}
        </p>
      )}

      {/* Os TRÊS estados são escritos, e o do meio é o que costuma faltar:
          sem curso escolhido a tela CONVIDA, em vez de mostrar uma lista vazia
          que se lê como "este curso não tem turma". */}
      {!curso ? (
        <p className="surface rounded-cf-lg p-4 text-sm text-cinza">
          Escolha o curso acima para ver as turmas dele.
        </p>
      ) : carregando && turmas === null ? (
        // O loader só na PRIMEIRA leitura do curso. Na releitura depois de um ato
        // (salvar a grade, marcar o horário), trocar a lista pelo loader
        // DESMONTAVA o editor aberto — e a resposta do banco ("Grade salva —
        // versão 2, e a anterior ficou guardada") sumia no mesmo instante em que
        // aparecia. Quem viu foi a prova de tela da G8 (000317), que não
        // conseguia ler a frase que ela existe para conferir.
        <Carregando />
      ) : (
        <div className="flex flex-col gap-3">
          {(turmas ?? []).length > 0 && <ResumoDaVirada turmas={turmas ?? []} />}
          {(turmas ?? []).map((t) => (
            <LinhaDaTurma
              key={t.turma_id}
              turma={t}
              editando={editando === t.turma_id}
              aoEditar={() => {
                setCronogramaDe(null);
                setInicioDe(null);
                setEditando(editando === t.turma_id ? null : t.turma_id);
              }}
              curso={curso}
              comCronograma={cronogramaDe === t.turma_id}
              aoAbrirCronograma={() => {
                setEditando(null);
                setInicioDe(null);
                setCronogramaDe(cronogramaDe === t.turma_id ? null : t.turma_id);
              }}
              comInicio={inicioDe === t.turma_id}
              aoAbrirInicio={() => {
                setEditando(null);
                setCronogramaDe(null);
                setInicioDe(inicioDe === t.turma_id ? null : t.turma_id);
              }}
              aoConcluirInicio={concluirInicio}
              aoTornarAtual={() => tornarAtual(t, t.definida_a_mao && t.atual)}
              aoExcluir={() => pedirExclusao(t)}
              aoMudarGrade={recarregar}
            />
          ))}
          {(turmas ?? []).length === 0 && (
            <p className="text-sm text-cinza">Este curso ainda não tem turma cadastrada.</p>
          )}
        </div>
      )}

      {/* ── O registro das turmas deste curso (000299) ───────────────────
          Fechado por padrão, num <details>: é a pergunta de quem investiga
          ("quem excluiu a turma 5?"), não a de quem opera todo dia, e aberto ele
          empurraria a lista de turmas para baixo. */}
      {curso && registro && (
        <details className="surface rounded-cf-lg p-4" data-registro-das-turmas={registro.total}>
          <summary className="cursor-pointer text-sm font-semibold text-tinta">
            Registro das turmas deste curso{registro.total > 0 ? ` — ${registro.total}` : ""}
          </summary>
          {registro.mudancas.length === 0 ? (
            <p className="mt-3 text-sm text-cinza">
              Nada registrado ainda. A criação e a exclusão de turma entram no registro desde
              25/09; o cronograma, desde 21/09.
            </p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borda text-left text-xs text-cinza">
                    <th className="py-2 pr-3 font-semibold">Quando</th>
                    <th className="py-2 pr-3 font-semibold">O que aconteceu</th>
                    <th className="py-2 pr-3 font-semibold">Por quê</th>
                    <th className="py-2 font-semibold">Quem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-borda">
                  {registro.mudancas.map((m) => (
                    <tr key={m.id} className="align-top" data-mudanca-da-turma={m.id}>
                      <td className="whitespace-nowrap py-2 pr-3 text-cinza">
                        {new Date(m.em).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="py-2 pr-3 text-tinta">{m.resumo}</td>
                      <td className="py-2 pr-3 text-cinza">{m.motivo ?? "—"}</td>
                      <td className="whitespace-nowrap py-2 text-cinza">{m.por}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>
      )}

      {aExcluir && (
        <PopupDeExclusao
          numero={aExcluir.turma.numero}
          ensaio={aExcluir.ensaio}
          excluindo={excluindo}
          recusa={recusaDaExclusao}
          aoConfirmar={confirmarExclusao}
          aoFechar={() => {
            setAExcluir(null);
            setRecusaDaExclusao(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * O popup de confirmação.
 *
 * `<dialog>` NATIVO, e com `m-auto`: o reset de CSS da casa zera a margem de
 * tudo, e sem essa classe o `inset: 0` das regras do navegador resolve para o
 * CANTO — foi assim que o modal do Mapa de Concursos abriu encostado no alto à
 * esquerda. Os outros três modais do hub já trazem a classe.
 *
 * O texto vem do ENSAIO que o banco devolveu, e não de uma frase montada aqui:
 * quantas disciplinas saem da grade e se ela é a turma atual são coisas que a
 * função já calculou, e escrever de novo criaria duas versões do mesmo aviso.
 */
function PopupDeExclusao({
  numero,
  ensaio,
  excluindo,
  recusa,
  aoConfirmar,
  aoFechar,
}: {
  numero: number;
  ensaio: RespostaDaExclusao;
  excluindo: boolean;
  /** A recusa do banco no clique de confirmar — hoje, a falta do motivo. */
  recusa: string | null;
  aoConfirmar: (motivo: string) => void;
  aoFechar: () => void;
}) {
  const dialogo = useRef<HTMLDialogElement>(null);
  // O MOTIVO (000299). Ele mora aqui dentro porque só existe enquanto o popup
  // está aberto: fechar e reabrir começa vazio, que é o certo para um ato que
  // não se repete.
  const [motivo, setMotivo] = useState("");

  useEffect(() => {
    dialogo.current?.showModal();
  }, []);

  return (
    <dialog
      ref={dialogo}
      onClose={aoFechar}
      aria-labelledby="titulo-excluir-turma"
      className="m-auto w-[min(32rem,92vw)] rounded-cf-lg bg-superficie p-0 text-tinta backdrop:bg-black/40"
    >
      <div className="p-5">
        <h3 id="titulo-excluir-turma" className="font-display text-lg font-bold text-titulo">
          Excluir a turma {numero}?
        </h3>
        <p className="mt-2 text-sm text-cinza">{ensaio.detalhe}</p>

        {/* O que NÃO acontece é dito com a mesma clareza do que acontece: foi a
            ressalva de Victor ao pedir a funcionalidade, e ela tem número — 185
            disciplinas servem mais de uma turma. */}
        <p className="mt-3 rounded-cf bg-aviso-fundo p-3 text-sm text-aviso-tinta">
          As avaliações <strong>não</strong> são excluídas. Elas continuam no curso e nas outras
          turmas que as cursam.
        </p>

        {ensaio.era_atual && (
          <p className="mt-2 rounded-cf bg-alerta-fundo p-3 text-sm text-alerta-tinta">
            Esta é a turma atual. Depois de excluí-la, as matrículas novas passam a cair em outra.
          </p>
        )}

        {/* O porquê é pedido ANTES do clique (o ensaio diz `pede_motivo`), e
            não descoberto pela recusa. Ele vai para o registro das turmas, com
            a fotografia da grade que sai. */}
        <label className="mt-4 flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-tinta">Por que esta turma sai?</span>
          <textarea
            data-campo="motivo-exclusao"
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="min-h-16 resize-y rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
            placeholder="Ex.: aberta por engano, sem aluno nenhum"
          />
          <span className="text-xs text-cinza">
            Fica no registro das turmas, com quem excluiu e a grade que ela tinha.
          </span>
        </label>

        {recusa && (
          <p role="alert" className="mt-3 rounded-cf bg-erro-fundo p-3 text-sm text-erro-tinta">
            {recusa}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => dialogo.current?.close()}
            className="min-h-11 rounded-full border border-borda px-5 text-sm font-semibold text-tinta"
          >
            Cancelar
          </button>
          {/* Sem `disabled` por motivo vazio: quem decide que ele é obrigatório
              é o BANCO, e a recusa dele diz o que falta — a mesma regra do
              motivo das outras telas do painel. */}
          <button
            type="button"
            data-confirmar-exclusao
            onClick={() => aoConfirmar(motivo)}
            disabled={excluindo}
            className="min-h-11 rounded-full bg-erro-tinta px-5 text-sm font-bold text-superficie disabled:opacity-60"
          >
            {excluindo ? "Excluindo…" : "Excluir a turma"}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * A virada do curso numa frase: onde caem as matrículas novas AGORA e quando
 * isso muda. O encerramento da turma atual é o início da próxima — derivado pelo
 * banco, e dito aqui uma vez só, em vez de repetido em cada linha.
 */
function ResumoDaVirada({ turmas }: { turmas: TurmaDoAdmin[] }) {
  const atual = turmas.find((t) => t.atual);
  const proxima = turmas[0]?.proxima_virada_numero ?? null;
  const quando = turmas[0]?.proxima_virada_texto ?? null;
  return (
    <p className="surface rounded-cf-lg p-4 text-sm text-tinta" data-proxima-virada={proxima ?? ""}>
      As matrículas novas caem na <strong>turma {atual?.numero ?? "—"}</strong>.{" "}
      {proxima !== null && quando ? (
        <>
          A partir de <strong>{quando}</strong> (horário de Brasília), vão para a{" "}
          <strong>turma {proxima}</strong> — é quando a turma {atual?.numero ?? "atual"} encerra.
        </>
      ) : (
        <span className="text-cinza">
          Nenhuma virada marcada. Para a próxima turma começar num dia e hora certos, marque o
          horário de início nela.
        </span>
      )}
    </p>
  );
}

function LinhaDaTurma({
  turma,
  editando,
  curso,
  comCronograma,
  aoAbrirCronograma,
  comInicio,
  aoAbrirInicio,
  aoConcluirInicio,
  aoEditar,
  aoTornarAtual,
  aoExcluir,
  aoMudarGrade,
}: {
  turma: TurmaDoAdmin;
  editando: boolean;
  curso: string;
  comCronograma: boolean;
  aoAbrirCronograma: () => void;
  comInicio: boolean;
  aoAbrirInicio: () => void;
  aoConcluirInicio: (r: RespostaDoInicio) => Promise<void>;
  aoEditar: () => void;
  aoTornarAtual: () => void;
  aoExcluir: () => void;
  aoMudarGrade: () => Promise<void>;
}) {
  return (
    <section className="surface rounded-cf-lg" data-turma={turma.numero}>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <h3 className="font-display text-base font-bold text-titulo">Turma {turma.numero}</h3>

        {turma.atual && (
          <span className="rounded-full bg-aviso-fundo px-2.5 py-1 text-xs font-bold text-aviso-tinta">
            turma atual
          </span>
        )}
        {turma.definida_a_mao && (
          <span className="rounded-full bg-chip px-2.5 py-1 text-xs font-semibold text-cinza">
            escolhida pela equipe
          </span>
        )}
        {/* Turma atual SEM grade é o defeito que a 000217 consertou, e a tela
            precisa dizê-lo: o aluno matriculado nela abre a Faculdade vazia. */}
        {turma.atual && turma.disciplinas === 0 && (
          <span className="rounded-full bg-alerta-fundo px-2.5 py-1 text-xs font-bold text-alerta-tinta">
            sem grade
          </span>
        )}
        {/* O horário de início (000316). O verbo muda com o tempo — "começa em"
            enquanto não chegou, "recebe desde" depois —, porque a mesma data
            responde a duas perguntas diferentes antes e depois dela. */}
        {turma.recebe_matriculas_desde_texto && (
          <span
            className="rounded-full bg-chip px-2.5 py-1 text-xs font-semibold text-cinza"
            data-inicio-da-turma={turma.numero}
          >
            {turma.ja_comecou ? "recebe matrículas desde " : "começa a receber matrículas em "}
            {turma.recebe_matriculas_desde_texto}
          </span>
        )}

        <span className="ml-auto text-sm text-cinza tabular-nums">
          {turma.disciplinas} {turma.disciplinas === 1 ? "disciplina" : "disciplinas"} ·{" "}
          {turma.matriculas.toLocaleString("pt-BR")}{" "}
          {turma.matriculas === 1 ? "matrícula" : "matrículas"}
        </span>

        <div className="flex gap-2">
          {/* Os TRÊS estados desta turma, e cada um com o seu botão:

                é a atual POR ESCOLHA  → "Voltar a derivar" (desfaz a escolha)
                é a atual POR DERIVAÇÃO → nada a fazer: o botão fica DESLIGADO
                não é a atual          → "Tornar a turma atual"

              O do meio é o que faltava, e Victor viu na tela: a turma já tinha o
              selo "turma atual" e o botão ainda oferecia torná-la atual. Clicar
              ali não era inócuo — fixava a derivação como escolha manual —, mas
              como CONVITE estava errado: ninguém lê "tornar atual" num item que
              a própria tela acabou de marcar como atual.

              Desligado PERMANENTE pede desenho próprio, não transparência (a
              medição que criou a `pill-desligada`: `opacity-50` sobre navy dá
              1,57 : 1). Aqui o botão perde a borda de ação e vira texto em
              `cinza`, com `title` dizendo POR QUE ele não serve. */}
          {turma.atual && !turma.definida_a_mao ? (
            <span
              className="inline-flex min-h-10 cursor-not-allowed items-center rounded-full border border-borda/50 px-4 text-sm font-semibold text-cinza"
              title="Esta já é a turma atual: ela é a mais recente do curso que tem grade. Para fixá-la mesmo que outra turma ganhe grade depois, escolha outra e volte."
            >
              Já é a turma atual
            </span>
          ) : (
            <button
              type="button"
              onClick={aoTornarAtual}
              className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-laranja"
            >
              {turma.atual && turma.definida_a_mao ? "Voltar a derivar" : "Tornar a turma atual"}
            </button>
          )}
          <button
            type="button"
            onClick={aoEditar}
            aria-expanded={editando}
            className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-laranja"
          >
            {editando ? "Fechar grade" : "Editar grade"}
          </button>
          <button
            type="button"
            data-abrir-cronograma={turma.turma_id}
            onClick={aoAbrirCronograma}
            aria-expanded={comCronograma}
            className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-laranja"
          >
            {comCronograma ? "Fechar cronograma" : "Cronograma"}
          </button>
          <button
            type="button"
            data-abrir-inicio={turma.turma_id}
            onClick={aoAbrirInicio}
            aria-expanded={comInicio}
            className="min-h-10 rounded-full border border-borda px-4 text-sm font-semibold text-tinta transition hover:border-laranja"
          >
            {comInicio ? "Fechar horário" : "Horário de início"}
          </button>
          {/* Excluir fica por ÚLTIMO e com cor de erro: ele é o único botão
              desta linha que não tem volta. E não some quando a turma tem
              matrícula — quem recusa é o banco, com o número de alunos, e é
              essa frase que o operador precisa ler. Botão que desaparece
              obriga a descobrir sozinho por que ele não está lá. */}
          <button
            type="button"
            data-excluir-turma={turma.numero}
            onClick={aoExcluir}
            className="min-h-10 rounded-full border border-erro-borda px-4 text-sm font-semibold text-erro-tinta transition hover:bg-erro-fundo"
          >
            Excluir
          </button>
        </div>
      </div>

      {editando && <EditorDaGrade turma={turma.turma_id} aoSalvar={aoMudarGrade} />}
      {comCronograma && (
        <AdminPosCronograma curso={curso} turma={turma.turma_id} aoSalvar={aoMudarGrade} />
      )}
      {comInicio && <EditorDoInicio turma={turma} aoConcluir={aoConcluirInicio} />}
    </section>
  );
}

/**
 * O horário em que a turma começa a receber as matrículas novas (000316).
 *
 * O campo é `datetime-local` e manda o valor COMO FOI DIGITADO — "2026-10-01T00:00"
 * —, porque o fuso é aplicado no banco. Converter aqui faria a hora depender do
 * relógio de quem clica; o banco sabe que é a hora de Brasília.
 *
 * A recusa (o mesmo instante de outra turma) fica AQUI, ao lado do campo que a
 * causou; o sucesso vira o recado da tela, porque ele fala do curso inteiro.
 */
function EditorDoInicio({
  turma,
  aoConcluir,
}: {
  turma: TurmaDoAdmin;
  aoConcluir: (r: RespostaDoInicio) => Promise<void>;
}) {
  const [valor, setValor] = useState(turma.recebe_matriculas_desde_local ?? "");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [recusa, setRecusa] = useState<string | null>(null);

  async function enviar(desde: string | null) {
    setSalvando(true);
    setRecusa(null);
    const r = await agendarTurma(turma.turma_id, desde, motivo.trim() || undefined);
    setSalvando(false);
    if (!r.ok) {
      setRecusa(r.detalhe ?? "Não deu para marcar o horário.");
      return;
    }
    await aoConcluir(r);
  }

  return (
    <div className="border-t border-borda p-4" data-editor-inicio={turma.turma_id}>
      <p className="text-sm text-cinza">
        A partir deste instante, as matrículas novas do curso vão para a turma {turma.numero} — e a
        turma que estiver recebendo encerra no mesmo instante. Horário de Brasília. Quem comprou antes
        continua na turma em que caiu, mesmo que a compra só seja processada depois.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-bold tracking-widest text-cinza uppercase">
            Recebe matrículas a partir de
          </span>
          <input
            type="datetime-local"
            data-campo="inicio"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            className="h-11 rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
          />
        </label>
        <label className="flex min-w-60 flex-1 flex-col gap-1.5">
          <span className="text-xs font-bold tracking-widest text-cinza uppercase">Motivo (opcional)</span>
          <input
            type="text"
            data-campo="motivo-inicio"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: abertura da turma de outubro"
            className="h-11 rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
          />
        </label>
      </div>
      {recusa && (
        <p role="alert" className="mt-3 rounded-cf bg-erro-fundo p-3 text-sm text-erro-tinta">
          {recusa}
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          data-salvar-inicio
          onClick={() => enviar(valor)}
          disabled={salvando || !valor}
          className={valor && !salvando ? "pill-cta min-h-11 px-5" : "pill-desligada min-h-11 px-5"}
        >
          {salvando ? "Salvando…" : "Salvar horário"}
        </button>
        {/* Tirar só existe onde há o que tirar. Sem horário, o curso volta à
            escolha da equipe ou à derivação de sempre — e a resposta diz qual
            turma passa a receber. */}
        {turma.recebe_matriculas_desde && (
          <button
            type="button"
            data-tirar-inicio
            onClick={() => enviar(null)}
            disabled={salvando}
            className="min-h-11 rounded-full border border-borda px-5 text-sm font-semibold text-tinta transition hover:border-laranja"
          >
            Tirar o horário
          </button>
        )}
      </div>
    </div>
  );
}

function EditorDaGrade({ turma, aoSalvar }: { turma: string; aoSalvar: () => Promise<void> }) {
  const [grade, setGrade] = useState<GradeDaTurma | null>(null);
  const [lista, setLista] = useState<DisciplinaNaGrade[]>([]);
  const [disponiveis, setDisponiveis] = useState<DisciplinaDisponivel[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [resposta, setResposta] = useState<RespostaDaGrade | null>(null);
  const [criando, setCriando] = useState(false);
  const [nova, setNova] = useState({ nome: "", carga: "", docente: "", titulacao: "" });
  const [erroDaNova, setErroDaNova] = useState<string | null>(null);
  const [salvandoNova, setSalvandoNova] = useState(false);
  const [questoesDe, setQuestoesDe] = useState<string | null>(null);
  // (000317) O porquê de ALTERAR a grade, e as versões que ela já teve. O motivo
  // mora no editor porque é da edição que está aberta; as versões são relidas a
  // cada `carregar`, que é o que todo ato daqui faz.
  const [motivo, setMotivo] = useState("");
  const [versoes, setVersoes] = useState<VersaoDaGrade[] | null>(null);
  const [recadoDaVersao, setRecadoDaVersao] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const g = await lerGradeDaTurma(turma);
    setGrade(g);
    setLista(g?.na_grade ?? []);
    setDisponiveis(g?.disponiveis ?? []);
    setMotivo("");
    setRecadoDaVersao(null);
    // As versões são peça SECUNDÁRIA: se a leitura falhar, o editor continua.
    setVersoes(await lerVersoesDaGrade(turma));
  }, [turma]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (!grade) return <div className="border-t border-borda p-4"><Carregando /></div>;

  const mudou =
    lista.length !== (grade.na_grade ?? []).length ||
    lista.some((d, i) => d.disciplina_id !== grade.na_grade[i]?.disciplina_id);

  function mover(i: number, delta: number) {
    const j = i + delta;
    if (j < 0 || j >= lista.length) return;
    const nova = [...lista];
    [nova[i], nova[j]] = [nova[j], nova[i]];
    setLista(nova);
    setResposta(null);
  }

  function remover(i: number) {
    const fora = lista[i];
    setLista(lista.filter((_, k) => k !== i));
    setDisponiveis(
      [
        ...disponiveis,
        {
          disciplina_id: fora.disciplina_id,
          nome: fora.nome,
          carga_horaria_horas: fora.carga_horaria_horas,
          docente_nome: fora.docente_nome,
          questoes_ativas: 0,
        },
      ].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
    );
    setResposta(null);
  }

  function acrescentar(d: DisciplinaDisponivel) {
    setLista([
      ...lista,
      {
        disciplina_id: d.disciplina_id,
        nome: d.nome,
        carga_horaria_horas: d.carga_horaria_horas,
        docente_nome: d.docente_nome,
        ordem: lista.length + 1,
        alunos_com_tentativa: 0,
      },
    ]);
    setDisponiveis(disponiveis.filter((x) => x.disciplina_id !== d.disciplina_id));
    setResposta(null);
  }

  async function salvar(confirmar: boolean) {
    setSalvando(true);
    const r = await definirGrade(
      turma,
      lista.map((d) => d.disciplina_id),
      confirmar,
      motivo.trim() || undefined,
    );
    setResposta(r);
    if (r.ok) {
      // O "Salvando…" dura até a RELEITURA acabar, e as setas ficam travadas
      // junto: a releitura chega depois e troca a lista inteira, e uma edição
      // feita nesse meio-tempo seria apagada por ela sem aviso. Quem achou foi a
      // prova de tela da G8, que reordenava logo depois de salvar.
      await carregar();
      await aoSalvar();
    }
    setSalvando(false);
  }

  // (000317) Carregar uma versão ANTIGA no editor — não grava nada: o operador
  // confere a lista e salva, com o motivo, e o salvar vira a versão nova. Voltar
  // atrás por um clique só gravaria sem ninguém olhar o que volta.
  //
  // Só volta o que ainda EXISTE no curso: a disciplina que saiu do catálogo
  // depois da versão não tem como voltar para a grade, e a tela diz quantas.
  function carregarVersao(v: VersaoDaGrade) {
    const noCurso = new Map<string, DisciplinaNaGrade>();
    for (const d of lista) noCurso.set(d.disciplina_id, d);
    for (const d of disponiveis) {
      noCurso.set(d.disciplina_id, {
        disciplina_id: d.disciplina_id,
        nome: d.nome,
        carga_horaria_horas: d.carga_horaria_horas,
        docente_nome: d.docente_nome,
        ordem: 0,
        alunos_com_tentativa: 0,
      });
    }
    const volta = v.disciplinas
      .map((x) => noCurso.get(x.disciplina_id))
      .filter((d): d is DisciplinaNaGrade => d !== undefined)
      .map((d, i) => ({ ...d, ordem: i + 1 }));
    const fora = v.disciplinas.length - volta.length;
    const naVolta = new Set(volta.map((d) => d.disciplina_id));
    setLista(volta);
    setDisponiveis(
      [...noCurso.values()]
        .filter((d) => !naVolta.has(d.disciplina_id))
        .map((d) => ({
          disciplina_id: d.disciplina_id,
          nome: d.nome,
          carga_horaria_horas: d.carga_horaria_horas,
          docente_nome: d.docente_nome,
          // A que estava NA grade não traz a contagem de questões (a leitura da
          // grade não a devolve). 10 aqui não é afirmação, é "sem aviso": o
          // número de verdade volta do banco no `carregar` depois de salvar.
          questoes_ativas: disponiveis.find((x) => x.disciplina_id === d.disciplina_id)?.questoes_ativas ?? 10,
        }))
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
    );
    setMotivo(`Voltar à versão ${v.versao}`);
    setResposta(null);
    setRecadoDaVersao(
      `A versão ${v.versao} está no editor. Confira a lista e salve — a grade de agora fica guardada como versão.` +
        (fora > 0 ? ` ${fora} disciplina(s) daquela versão não existem mais no curso e ficaram de fora.` : ""),
    );
  }

  const horas = lista.reduce((s, d) => s + (d.carga_horaria_horas ?? 0), 0);

  // Criar avaliação NA TURMA: a disciplina nasce e já entra na grade, num ato
  // só. O curso vem da própria grade (`pos_admin_grade` devolve `curso_slug`) —
  // a tela não o recebe por prop nem o adivinha, e assim não há como criar uma
  // disciplina num curso e pô-la na turma de outro.
  async function criarNova() {
    setSalvandoNova(true);
    setErroDaNova(null);
    const r = await criarAvaliacao({
      curso: grade!.curso_slug,
      nome: nova.nome,
      carga: nova.carga.trim() === "" ? null : Number(nova.carga),
      docente: nova.docente || null,
      titulacao: nova.titulacao || null,
      turma,
    });
    setSalvandoNova(false);
    if (!r.ok) {
      setErroDaNova(r.detalhe ?? "Não deu para criar a avaliação.");
      return;
    }
    setCriando(false);
    setNova({ nome: "", carga: "", docente: "", titulacao: "" });
    await carregar();
    await aoSalvar();
    // Abre as questões da avaliação recém-criada: ela nasce vazia, e sem 10
    // questões o aluno não consegue fazê-la. Mandar o operador procurar o botão
    // depois seria esconder o passo que falta.
    if (r.disciplina_id) setQuestoesDe(r.disciplina_id);
  }

  return (
    <div className="border-t border-borda p-4">
      <div className="grid gap-5 lg:grid-cols-2">
        <div>
          <h4 className="text-xs font-bold tracking-widest text-cinza uppercase">
            Na grade ({lista.length}) · {horas}h
          </h4>
          <ol className="mt-2 flex flex-col gap-1.5">
            {lista.map((d, i) => (
              <li
                key={d.disciplina_id}
                className="flex items-center gap-2 rounded-cf border border-borda bg-superficie p-2.5"
                data-disciplina={d.disciplina_id}
              >
                <span className="w-6 text-center text-xs font-bold text-cinza tabular-nums">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-tinta">{d.nome}</p>
                  <p className="text-xs text-cinza">
                    {d.carga_horaria_horas ?? "—"}h
                    {d.docente_nome ? ` · ${d.docente_nome}` : ""}
                    {/* O número que decide se dá para remover. Ele vem do banco,
                        por turma — não é "quantos fizeram a disciplina", é
                        quantos DESTA turma. */}
                    {d.alunos_com_tentativa > 0 && (
                      <span className="text-alerta-tinta">
                        {" "}
                        · {d.alunos_com_tentativa}{" "}
                        {d.alunos_com_tentativa === 1 ? "aluno já fez prova" : "alunos já fizeram prova"}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => mover(i, -1)}
                  disabled={i === 0 || salvando}
                  aria-label={`Subir ${d.nome}`}
                  className="h-8 w-8 rounded-cf border border-borda text-sm text-tinta disabled:opacity-40"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => mover(i, 1)}
                  disabled={i === lista.length - 1 || salvando}
                  aria-label={`Descer ${d.nome}`}
                  className="h-8 w-8 rounded-cf border border-borda text-sm text-tinta disabled:opacity-40"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => remover(i)}
                  disabled={salvando}
                  aria-label={`Tirar ${d.nome} da grade`}
                  className="h-8 rounded-cf border border-borda px-2.5 text-xs font-semibold text-tinta transition hover:border-laranja"
                >
                  Tirar
                </button>
                {/* As QUESTÕES ficam aqui, na disciplina, e não numa aba
                    própria: a pergunta "esta avaliação abre?" só faz sentido ao
                    lado da grade em que ela está. O editor abre embaixo da
                    linha, sem tirar o operador de onde ele estava. */}
                <button
                  type="button"
                  onClick={() =>
                    setQuestoesDe(questoesDe === d.disciplina_id ? null : d.disciplina_id)
                  }
                  aria-expanded={questoesDe === d.disciplina_id}
                  className="h-8 rounded-cf border border-borda px-2.5 text-xs font-semibold text-tinta transition hover:border-laranja"
                >
                  Questões
                </button>
              </li>
            ))}
            {questoesDe && lista.some((d) => d.disciplina_id === questoesDe) && (
              <li className="mt-1 rounded-cf border border-acento/40 bg-superficie-2 p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h5 className="font-display text-sm font-bold text-titulo">
                    Questões de {lista.find((d) => d.disciplina_id === questoesDe)?.nome}
                  </h5>
                  <button
                    type="button"
                    onClick={() => setQuestoesDe(null)}
                    className="text-xs font-semibold text-link underline"
                  >
                    Fechar
                  </button>
                </div>
                <AdminPosQuestoes disciplina={questoesDe} aoMudar={carregar} />
              </li>
            )}
            {lista.length === 0 && (
              <li className="rounded-cf bg-alerta-fundo p-3 text-sm text-alerta-tinta">
                Sem nenhuma disciplina, o aluno desta turma abre a Faculdade vazia.
              </li>
            )}
          </ol>
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-xs font-bold tracking-widest text-cinza uppercase">
              Disponíveis no curso ({disponiveis.length})
            </h4>
            {!criando && (
              <button
                type="button"
                onClick={() => setCriando(true)}
                className="h-8 rounded-cf border border-borda px-2.5 text-xs font-semibold text-tinta transition hover:border-laranja"
              >
                Criar avaliação
              </button>
            )}
          </div>

          {criando && (
            <div className="mt-2 rounded-cf border border-acento/40 bg-superficie-2 p-3">
              <p className="text-xs font-bold tracking-widest text-cinza uppercase">
                Nova avaliação nesta turma
              </p>
              <input
                value={nova.nome}
                onChange={(e) => {
                  setNova({ ...nova, nome: e.target.value });
                  setErroDaNova(null);
                }}
                placeholder="Nome da avaliação"
                aria-label="Nome da avaliação"
                className="mt-2 h-11 w-full rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
              />
              <div className="mt-2 flex gap-2">
                <input
                  value={nova.carga}
                  onChange={(e) => setNova({ ...nova, carga: e.target.value.replace(/\D/g, "") })}
                  placeholder="Horas"
                  inputMode="numeric"
                  aria-label="Carga horária em horas"
                  className="h-11 w-24 rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
                />
                <input
                  value={nova.docente}
                  onChange={(e) => setNova({ ...nova, docente: e.target.value })}
                  placeholder="Docente"
                  aria-label="Nome do docente"
                  className="h-11 flex-1 rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
                />
                <input
                  value={nova.titulacao}
                  onChange={(e) => setNova({ ...nova, titulacao: e.target.value })}
                  placeholder="Titulação"
                  aria-label="Titulação do docente"
                  className="h-11 w-32 rounded-cf border border-borda bg-superficie px-3 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
                />
              </div>
              {/* O aviso ANTES de criar, e não depois: a prova é de 10 questões,
                  e uma avaliação vazia na grade é uma que o aluno não consegue
                  fazer. Quem descobrisse isso pelo aluno já teria a turma
                  matriculada. */}
              <p className="mt-2 text-xs text-cinza">
                Ela entra na grade desta turma. A prova é de 10 questões — o editor abre em seguida.
              </p>
              {erroDaNova && (
                <p className="mt-2 rounded-cf bg-erro-fundo p-2.5 text-sm text-erro-tinta">
                  {erroDaNova}
                </p>
              )}
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={criarNova}
                  disabled={nova.nome.trim() === "" || salvandoNova}
                  className={
                    nova.nome.trim() !== "" && !salvandoNova
                      ? "pill-cta min-h-10 px-4"
                      : "pill-desligada min-h-10 px-4"
                  }
                >
                  {salvandoNova ? "Criando…" : "Criar e pôr na grade"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCriando(false);
                    setErroDaNova(null);
                  }}
                  className="text-sm font-semibold text-link underline"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
          <ul className="mt-2 flex max-h-96 flex-col gap-1.5 overflow-y-auto">
            {disponiveis.map((d) => (
              <li
                key={d.disciplina_id}
                className="flex items-center gap-2 rounded-cf border border-borda bg-superficie p-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-tinta">{d.nome}</p>
                  <p className="text-xs text-cinza">
                    {d.carga_horaria_horas ?? "—"}h
                    {d.docente_nome ? ` · ${d.docente_nome}` : ""}
                    {/* Menos de 10 questões ativas DESABILITA a prova. Dizer isto
                        aqui evita a turma ganhar uma disciplina que o aluno não
                        consegue cursar. */}
                    {d.questoes_ativas < 10 && (
                      <span className="text-alerta-tinta"> · {d.questoes_ativas} questões — a prova não abre</span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => acrescentar(d)}
                  disabled={salvando}
                  className="h-8 rounded-cf border border-borda px-2.5 text-xs font-semibold text-tinta transition hover:border-laranja"
                >
                  Pôr na grade
                </button>
              </li>
            ))}
            {disponiveis.length === 0 && (
              <li className="text-sm text-cinza">Todas as disciplinas do curso já estão nesta grade.</li>
            )}
          </ul>
        </div>
      </div>

      {/* A recusa do banco quando a remoção alcança aluno que já fez prova. Ela
          não é erro: é a pergunta que falta, com o número ao lado. */}
      {resposta && !resposta.ok && resposta.motivo === "remocao_afeta_alunos" && (
        <div className="mt-4 rounded-cf-lg border border-alerta-tinta/40 bg-alerta-fundo p-3">
          <p className="text-sm font-semibold text-alerta-tinta">{resposta.detalhe}</p>
          <ul className="mt-2 flex flex-col gap-0.5">
            {(resposta.afetados ?? []).map((a) => (
              <li key={a.disciplina_id} className="text-xs text-alerta-tinta">
                {a.nome} — {a.alunos} {a.alunos === 1 ? "aluno" : "alunos"}
              </li>
            ))}
          </ul>
          {/* (000317) Confirmar também pede o porquê — e a tela diz ANTES do
              clique, porque o banco avisou (`pede_motivo`). */}
          {resposta.pede_motivo && !motivo.trim() && (
            <p className="mt-2 text-xs font-semibold text-alerta-tinta">
              Escreva abaixo por que a grade muda: aplicar também pede o porquê.
            </p>
          )}
          <button
            type="button"
            onClick={() => salvar(true)}
            disabled={salvando}
            className="mt-3 min-h-10 rounded-full bg-alerta-tinta px-4 text-sm font-bold text-superficie"
          >
            Aplicar mesmo assim
          </button>
        </div>
      )}

      {resposta && !resposta.ok && resposta.motivo !== "remocao_afeta_alunos" && (
        <p className="mt-4 rounded-cf-lg bg-erro-fundo p-3 text-sm text-erro-tinta">
          {resposta.detalhe ?? "Não deu para salvar a grade."}
        </p>
      )}

      {resposta?.ok && (
        <p role="status" className="mt-4 rounded-cf-lg bg-aviso-fundo p-3 text-sm text-aviso-tinta" data-grade-salva>
          {resposta.sem_mudanca ? (
            "Nada mudou na grade."
          ) : (
            <>
              Grade salva: {resposta.total} disciplina(s)
              {(resposta.entrando ?? 0) > 0 ? `, ${resposta.entrando} nova(s)` : ""}
              {(resposta.saindo ?? 0) > 0 ? `, ${resposta.saindo} removida(s)` : ""}
              {resposta.versao ? ` — versão ${resposta.versao}, e a anterior ficou guardada` : ""}.
            </>
          )}
        </p>
      )}

      {recadoDaVersao && (
        <p role="status" className="mt-4 rounded-cf-lg bg-alerta-fundo p-3 text-sm text-alerta-tinta" data-versao-no-editor>
          {recadoDaVersao}
        </p>
      )}

      {/* (000317) O PORQUÊ de alterar uma grade que existe. Só aparece quando o
          salvar vai ALTERAR: a primeira grade de uma turma vazia é criação e não
          pede motivo — quem decide isto é o banco, e a tela só não pergunta à
          toa. */}
      {(grade.na_grade ?? []).length > 0 && mudou && (
        <label className="mt-4 flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-tinta">Por que a grade muda?</span>
          <textarea
            data-campo="motivo-grade"
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: a disciplina de Perícia Trabalhista entra no lugar da de Custos, a pedido da coordenação"
            className="min-h-16 resize-y rounded-cf border border-borda bg-superficie px-3 py-2 text-sm text-tinta focus:border-acento focus:ring-3 focus:ring-acento/25 focus:outline-none"
          />
          <span className="text-xs text-cinza">
            A grade de agora fica guardada como versão, e o motivo fica com a nova.
          </span>
        </label>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => salvar(false)}
          disabled={!mudou || salvando}
          className={mudou && !salvando ? "pill-cta min-h-11 px-5" : "pill-desligada min-h-11 px-5"}
        >
          {salvando ? "Salvando…" : "Salvar a grade"}
        </button>
        {mudou && (
          <button
            type="button"
            onClick={() => void carregar()}
            className="text-sm font-semibold text-link underline"
          >
            Descartar
          </button>
        )}
      </div>

      {/* ── As VERSÕES da grade (000317) ─────────────────────────────────────
          Fechado por padrão, como o registro das turmas: é a pergunta de quem
          investiga ("o que esta turma cursava em março?"), não a de quem edita
          todo dia. A vigente é a de cima. */}
      {versoes && versoes.length > 0 && (
        <details className="mt-5 rounded-cf-lg border border-borda p-3" data-versoes-da-grade={versoes.length}>
          <summary className="cursor-pointer text-sm font-semibold text-tinta">
            Versões da grade — {versoes.length}
          </summary>
          <ol className="mt-3 flex flex-col gap-2">
            {versoes.map((v) => (
              <li key={v.versao} className="rounded-cf border border-borda p-2.5" data-versao={v.versao}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold text-tinta">Versão {v.versao}</span>
                  {v.vigente && (
                    <span className="rounded-full bg-aviso-fundo px-2 py-0.5 text-xs font-bold text-aviso-tinta">
                      vigente
                    </span>
                  )}
                  <span className="text-xs text-cinza">
                    {v.em_texto} · {v.por} · {v.total_disciplinas} disciplina(s), {v.total_horas}h
                  </span>
                  {!v.vigente && (
                    <button
                      type="button"
                      data-carregar-versao={v.versao}
                      onClick={() => carregarVersao(v)}
                      disabled={salvando}
                      className="ml-auto h-8 rounded-cf border border-borda px-2.5 text-xs font-semibold text-tinta transition hover:border-laranja"
                    >
                      Carregar no editor
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-cinza">
                  {v.origem === "inicial"
                    ? "A grade como estava quando o histórico começou (26/09/2026)."
                    : (v.motivo ?? "Sem motivo — era a primeira grade da turma.")}
                </p>
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs font-semibold text-link">As disciplinas</summary>
                  <ol className="mt-1 list-decimal pl-5 text-xs text-tinta">
                    {v.disciplinas.map((d) => (
                      <li key={d.disciplina_id}>
                        {d.nome} — {d.carga_horaria_horas ?? "—"}h{d.docente_nome ? ` · ${d.docente_nome}` : ""}
                      </li>
                    ))}
                  </ol>
                </details>
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}
